import React, { useEffect, useState } from 'react';
import { Inputs, PackageType, BuildPayType, IndustryType, INDUSTRY_DEFAULTS } from '../utils/roiCalculations';
interface CalculatorInputsProps {
  inputs: Inputs;
  onChange: (newInputs: Inputs) => void;
  onReset?: () => void;
  onReview?: () => void;
  showActionButtons?: boolean;
}
export function CalculatorInputs({
  inputs,
  onChange,
  onReset,
  onReview,
  showActionButtons = true
}: CalculatorInputsProps) {
  const [expandedSections, setExpandedSections] = useState({
    companyInfo: true,
    campaignAdSpend: false,
    revenueMargins: false,
    salesPerformance: false,
    retentionModel: false
  });
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChange = (field: keyof Inputs, value: string | number) => {
    onChange({
      ...inputs,
      [field]: value
    });
  };
  // Update industry-specific defaults when industry changes
  const handleIndustryChange = (newIndustry: IndustryType) => {
    const defaults = INDUSTRY_DEFAULTS[newIndustry];
    onChange({
      ...inputs,
      industry: newIndustry,
      ticket: defaults.ticket,
      gpm: defaults.gpm,
      salesFirst90Total: defaults.salesFirst90Total,
      salesVetMonthly: defaults.salesVetMonthly
    });
  };
  return <div className="bg-white border-2 border-gray-200 rounded p-6 md:p-8 shadow-lg" data-id="element-1">
      <div className="space-y-6" data-id="element-2">
        {/* Company Information - Single Row (No Collapsible) */}
        <div className="bg-gray-50 p-4 rounded border-2 border-gray-200" data-id="element-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4">
            {/* Company Name */}
            <div data-id="element-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6">
                Company Name
              </label>
              <input type="text" value={inputs.companyName} onChange={e => handleChange('companyName', e.target.value)} placeholder="Enter company name" className="w-full p-3 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-7" />
            </div>

            {/* Industry */}
            <div data-id="element-8">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9">
                Industry
              </label>
              <select value={inputs.industry} onChange={e => handleIndustryChange(e.target.value as IndustryType)} className="w-full p-3 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-10">
                <option value="" data-id="element-11">Select</option>
                <option data-id="element-12">Windows</option>
                <option data-id="element-13">Roofing</option>
                <option data-id="element-14">Solar (Sales)</option>
                <option data-id="element-15">Solar (Vertical)</option>
                <option data-id="element-16">Solar (Community)</option>
                <option data-id="element-17">Pest</option>
                <option data-id="element-18">Fiber</option>
                <option data-id="element-19">Deregulated Energy</option>
                <option data-id="element-20">Hardscaping</option>
                <option data-id="element-21">Landscaping</option>
                <option data-id="element-22">Other</option>
              </select>
            </div>

            {/* Partnership */}
            <div data-id="element-23">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-24">
                Partnership
              </label>
              <select value={inputs.pkg} onChange={e => handleChange('pkg', e.target.value as PackageType)} className="w-full p-3 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm font-semibold" data-id="element-25">
                <option value="" data-id="element-26">Select</option>
                <option value="edge" data-id="element-27">HIRE Edge</option>
                <option value="advantage" data-id="element-28">HIRE Advantage</option>
                <option value="summit" data-id="element-29">HIRE Summit</option>
              </select>
            </div>
          </div>
        </div>

        {/* Campaign & Ad Spend - Collapsible */}
        <div data-id="element-30">
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded cursor-pointer hover:bg-blue-100 transition-colors border-2 border-blue-200" onClick={() => toggleSection('campaignAdSpend')} data-id="element-31">
            <h3 className="text-base md:text-lg font-semibold text-navy" data-id="element-32">
              Campaign & Ad Spend
            </h3>
            <span className="text-navy font-bold text-lg" data-id="element-33">
              {expandedSections.campaignAdSpend ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.campaignAdSpend && <div className="mt-4 bg-blue-50 p-4 rounded border-2 border-blue-200" data-id="element-34">
              <div className="space-y-3 flex flex-col items-center" data-id="element-35">
                <div className="flex items-center gap-4 w-full max-w-2xl" data-id="element-36">
                  <label className="text-sm font-semibold text-gray-700 w-48 text-right" data-id="element-37">
                    Number of Campaigns
                  </label>
                  <input type="number" min="1" step="1" value={inputs.campaigns} onChange={e => handleChange('campaigns', parseInt(e.target.value) || 0)} className="w-64 p-3 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-38" />
                </div>

                <div className="flex items-center gap-4 w-full max-w-2xl" data-id="element-39">
                  <label className="text-sm font-semibold text-gray-700 w-48 text-right" data-id="element-40">
                    Ad Spend per Month (per campaign)
                  </label>
                  <div className="w-64 relative" data-id="element-41">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-42">
                      $
                    </span>
                    <input type="number" min="0" step="100" value={inputs.adSpend} onChange={e => handleChange('adSpend', parseFloat(e.target.value) || 0)} className="w-full p-3 pl-8 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-43" />
                  </div>
                </div>

                <div className="flex items-center gap-4 w-full max-w-2xl" data-id="element-44">
                  <label className="text-sm font-semibold text-gray-700 w-48 text-right" data-id="element-45">
                    Ad Cost per Hire
                  </label>
                  <div className="w-64 relative" data-id="element-46">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-47">
                      $
                    </span>
                    <input type="number" min="1" step="10" value={inputs.cph} onChange={e => handleChange('cph', parseFloat(e.target.value) || 0)} className="w-full p-3 pl-8 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-48" />
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Revenue & Margins - Collapsible */}
        <div data-id="element-49">
          <div className="flex items-center justify-between p-4 bg-green-50 rounded cursor-pointer hover:bg-green-100 transition-colors border-2 border-green-200" onClick={() => toggleSection('revenueMargins')} data-id="element-50">
            <h3 className="text-base md:text-lg font-semibold text-navy" data-id="element-51">
              Revenue & Margins
            </h3>
            <span className="text-navy font-bold text-lg" data-id="element-52">
              {expandedSections.revenueMargins ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.revenueMargins && <div className="mt-4 bg-green-50 p-4 rounded border-2 border-green-200" data-id="element-53">
              <div className="space-y-3 flex flex-col items-center" data-id="element-54">
                <div className="flex items-center gap-4 w-full max-w-2xl" data-id="element-55">
                  <label className="text-sm font-semibold text-gray-700 w-48 text-right" data-id="element-56">
                    Average Ticket (Revenue per Sale)
                  </label>
                  <div className="w-64 relative" data-id="element-57">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-58">
                      $
                    </span>
                    <input type="number" min="0" step="500" value={inputs.ticket} onChange={e => handleChange('ticket', parseFloat(e.target.value) || 0)} className="w-full p-3 pl-8 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-59" />
                  </div>
                </div>

                <div className="flex items-center gap-4 w-full max-w-2xl" data-id="element-60">
                  <label className="text-sm font-semibold text-gray-700 w-48 text-right" data-id="element-61">
                    Gross Profit Margin
                  </label>
                  <div className="w-64 relative" data-id="element-62">
                    <input type="number" min="0" max="100" step="1" value={inputs.gpm} onChange={e => handleChange('gpm', parseFloat(e.target.value) || 0)} className="w-full p-3 pr-8 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-63" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-64">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Sales Performance - Collapsible */}
        <div data-id="element-65">
          <div className="flex items-center justify-between p-4 bg-amber-50 rounded cursor-pointer hover:bg-amber-100 transition-colors border-2 border-amber-200" onClick={() => toggleSection('salesPerformance')} data-id="element-66">
            <h3 className="text-base md:text-lg font-semibold text-navy" data-id="element-67">
              Sales Performance
            </h3>
            <span className="text-navy font-bold text-lg" data-id="element-68">
              {expandedSections.salesPerformance ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.salesPerformance && <div className="mt-4 bg-amber-50 p-4 rounded border-2 border-amber-200" data-id="element-69">
              <div className="space-y-3 flex flex-col items-center" data-id="element-70">
                <div className="flex items-center gap-4 w-full max-w-2xl" data-id="element-71">
                  <label className="text-sm font-semibold text-gray-700 w-48 text-right" data-id="element-72">
                    Sales per 90 Day Rep
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesFirst90Total} onChange={e => handleChange('salesFirst90Total', parseFloat(e.target.value) || 0)} className="w-64 p-3 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-73" />
                </div>

                <div className="flex items-center gap-4 w-full max-w-2xl" data-id="element-74">
                  <label className="text-sm font-semibold text-gray-700 w-48 text-right" data-id="element-75">
                    Monthly Sales per Veteran
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesVetMonthly} onChange={e => handleChange('salesVetMonthly', parseFloat(e.target.value) || 0)} className="w-64 p-3 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-76" />
                </div>

                <div className="flex items-center gap-4 w-full max-w-2xl" data-id="element-77">
                  <label className="text-sm font-semibold text-gray-700 w-48 text-right" data-id="element-78">
                    Pullthrough / Realization
                  </label>
                  <div className="w-64 relative" data-id="element-79">
                    <input type="number" min="0" max="100" step="1" value={inputs.pullthrough} onChange={e => handleChange('pullthrough', parseFloat(e.target.value) || 0)} className="w-full p-3 pr-8 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-80" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-81">
                      %
                    </span>
                  </div>
                </div>
              </div>

              
            </div>}
        </div>

        {/* Retention Model - Collapsible */}
        <div data-id="element-82">
          <div className="flex items-center justify-between p-4 bg-purple-50 rounded cursor-pointer hover:bg-purple-100 transition-colors border-2 border-purple-200" onClick={() => toggleSection('retentionModel')} data-id="element-83">
            <h3 className="text-base md:text-lg font-semibold text-navy" data-id="element-84">
              Retention Model
            </h3>
            <span className="text-navy font-bold text-lg" data-id="element-85">
              {expandedSections.retentionModel ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.retentionModel && <div className="mt-4 bg-purple-50 p-4 rounded border-2 border-purple-200" data-id="element-86">
              <div className="space-y-3 flex flex-col items-center" data-id="element-87">
                <div className="flex items-center gap-4 w-full max-w-2xl" data-id="element-88">
                  <label className="text-sm font-semibold text-gray-700 w-48 text-right" data-id="element-89">
                    Hired to Launched
                  </label>
                  <div className="w-64 relative" data-id="element-90">
                    <input type="number" min="0" max="100" step="1" value={inputs.pctLaunched} onChange={e => handleChange('pctLaunched', parseFloat(e.target.value) || 0)} className="w-full p-3 pr-8 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-91" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-92">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4 w-full max-w-2xl" data-id="element-93">
                  <label className="text-sm font-semibold text-gray-700 w-48 text-right" data-id="element-94">
                    Launched to Day 30
                  </label>
                  <div className="w-64 relative" data-id="element-95">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct30} onChange={e => handleChange('pct30', parseFloat(e.target.value) || 0)} className="w-full p-3 pr-8 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-96" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-97">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4 w-full max-w-2xl" data-id="element-98">
                  <label className="text-sm font-semibold text-gray-700 w-48 text-right" data-id="element-99">
                    Day 30 to Day 90
                  </label>
                  <div className="w-64 relative" data-id="element-100">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct90} onChange={e => handleChange('pct90', parseFloat(e.target.value) || 0)} className="w-full p-3 pr-8 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-101" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-102">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4 w-full max-w-2xl" data-id="element-103">
                  <label className="text-sm font-semibold text-gray-700 w-48 text-right" data-id="element-104">
                    Day 90 to 1 Year
                  </label>
                  <div className="w-64 relative" data-id="element-105">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct1y} onChange={e => handleChange('pct1y', parseFloat(e.target.value) || 0)} className="w-full p-3 pr-8 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-106" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-107">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {showActionButtons && onReset && <>
            <div className="border-t-2 border-gray-200 pt-6" data-id="element-108">
              <button onClick={onReset} className="w-full px-4 py-3 rounded border-2 border-gray-300 text-gray-700 hover:border-gray-400 font-bold text-sm transition-colors active:translate-y-[1px]" data-id="element-109">
                Reset to Defaults
              </button>
            </div>

            <div className="text-sm text-gray-600 leading-relaxed border-t-2 border-gray-200 pt-6" data-id="element-110">
              LMS note: access to a Learning Management System is included in
              all packages. In Edge it's more "DIY reference"; in
              Advantage/Summit it reinforces hands-on execution.
            </div>
          </>}

        {onReview && <div className="border-t-2 border-gray-200 pt-6" data-id="element-111">
            <button onClick={onReview} className="w-full py-4 md:py-5 rounded font-bold text-base md:text-lg transition-all shadow-lg bg-amber hover:bg-amber-dark text-navy active:translate-y-[1px] cursor-pointer" data-id="element-112">
              Review Your Numbers →
            </button>
          </div>}
      </div>
    </div>;
}